import { useState, useCallback, useMemo } from "react";
import { useDebounce } from "@/hooks/use-debounce";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import { useToast } from "@/components/ui/use-toast";
import type { StockRawRecord } from "@/components/stocks-raw/types";
import { useAddToPortfolio } from "@/hooks/portfolio/useAddToPortfolio";

interface UseStocksRawExplorerLogicParams {
  initialSearchText?: string;
  initialType?: string;
  initialExchange?: string;
}

export const useStocksRawExplorerLogic = ({
  initialSearchText = "",
  initialType = "",
  initialExchange = ""
}: UseStocksRawExplorerLogicParams = {}) => {
  const { toast } = useToast();
  const { openFundsRaw } = useAddToPortfolio();

  // === FILTER STATES ===
  const [searchText, setSearchText] = useState<string>(initialSearchText);
  const [selectedType, setSelectedType] = useState<string | null>(initialType ? initialType : null);
  const [selectedExchange, setSelectedExchange] = useState<string | null>(initialExchange ? initialExchange : null);
  
  // Use debounced search to reduce API calls
  const debouncedSearchText = useDebounce(searchText, 500);
  
  // === COUNT STATES ===
  const [totalCount, setTotalCount] = useState<number>(0);
  const [visibleCount, setVisibleCount] = useState<number>(0);
  
  // === PORTFOLIO DIALOG STATES ===
  const [isPortfolioDialogOpen, setIsPortfolioDialogOpen] = useState(false);
  const [selectedStock, setSelectedStock] = useState<StockRawRecord | null>(null);
  const [selectedStocks, setSelectedStocks] = useState<StockRawRecord[]>([]);
  const [isBulkMode, setIsBulkMode] = useState(false);
  
  // === TABLE CALLBACK STATE ===
  const [getSelectedStocksData, setGetSelectedStocksData] = useState<(() => StockRawRecord[]) | null>(null);
  
  // === PROCESSING STATE ===
  const [isProcessing, setIsProcessing] = useState(false);

  // === BULK SELECTION ===
  const [bulkSelectionState, bulkSelectionActions] = useBulkSelection(totalCount);

  // === EVENT HANDLERS ===
  
  // Handler for updating counts in filter display
  const handleCountsUpdate = useCallback((counts: { total: number; filtered: number; visible: number }) => {
    setTotalCount(counts.total);
    setVisibleCount(counts.visible);
  }, []);

  // Handle favorite button click
  const handleFavorite = useCallback((stockId: string) => {
    console.log("Add to favorites:", stockId);
    toast({
      title: "Added to Favorites",
      description: "Stock added to your favorites",
    });
  }, [toast]);

  // Handle add to watchlist button click
  const handleAddToWatchlist = useCallback((stockId: string) => {
    console.log("Add to watchlist:", stockId);
    toast({
      title: "Added to Watchlist",
      description: "Stock added to your watchlist",
    });
  }, [toast]);

  // Success handler used by both single and bulk flows
  const handlePortfolioSuccess = useCallback(() => {
    toast({
      title: "Success",
      description: isBulkMode 
        ? `${selectedStocks.length} stocks added to portfolio successfully`
        : "Stock added to portfolio successfully",
    });
    if (isBulkMode) {
      bulkSelectionActions.clearSelection();
    }
    setIsPortfolioDialogOpen(false);
  }, [isBulkMode, selectedStocks.length, bulkSelectionActions, toast]);

  // Handle add to portfolio button click
  const handleAddToPortfolio = useCallback((stockId: string, stock?: StockRawRecord) => {
    console.log("Add to portfolio:", stockId);
    if (stock) {
      // Map stock to fund shape and open global dialog
      const fund = {
        id: stock.id,
        display_name: `${stock.name} (${stock.code})`,
        name: stock.name,
        fund_name: stock.name,
        management_company_code: stock.exchange,
        company_name: stock.exchange_name || stock.exchange,
        fund_code: stock.code,
        description: `${stock.name} (${stock.code})`,
        sa_asset_class: 'stocks',
        asset_class: 'stocks',
        sa_sub_asset_class: stock.gicgroup || stock.gicsector || 'Stock',
        creation_date: new Date().toISOString(),
        ticker: stock.code,
        ...stock
      } as any;
      // Keep local state unchanged for now; delegate showing dialog to provider
      openFundsRaw({ fund, onSuccess: () => { handlePortfolioSuccess(); } });
    }
  }, [openFundsRaw, handlePortfolioSuccess]);

  // === BULK ACTION HANDLERS ===
  
  const handleBulkAddToPortfolio = useCallback(async () => {
    if (isProcessing) return;
    
    const selectedIds = Array.from(bulkSelectionState.selectedIds);
    if (selectedIds.length === 0) {
      toast({
        title: "No Selection",
        description: "Please select stocks to add to portfolio",
        variant: "destructive",
      });
      return;
    }
    
    // Get the actual selected stock data from the callback if available
    if (!getSelectedStocksData || typeof getSelectedStocksData !== 'function') {
      toast({
        title: "Loading",
        description: "Please wait for the table to load before selecting stocks",
        variant: "destructive",
      });
      return;
    }
    
    try {
      const selectedStockData = getSelectedStocksData();
      setSelectedStocks(selectedStockData);
      setIsBulkMode(true);
      // Map to funds and open global dialog
      const funds = selectedStockData.map(stock => ({
        id: stock.id,
        display_name: `${stock.name} (${stock.code})`,
        name: stock.name,
        fund_name: stock.name,
        management_company_code: stock.exchange,
        company_name: stock.exchange_name || stock.exchange,
        fund_code: stock.code,
        description: `${stock.name} (${stock.code})`,
        sa_asset_class: 'stocks',
        asset_class: 'stocks',
        sa_sub_asset_class: stock.gicgroup || stock.gicsector || 'Stock',
        creation_date: new Date().toISOString(),
        ticker: stock.code,
        ...stock
      })) as any[];
      openFundsRaw({ funds, onSuccess: () => { handlePortfolioSuccess(); } });
    } catch (error) {
      console.error('Error getting selected stocks:', error);
      toast({
        title: "Error",
        description: "Failed to get selected stocks data",
        variant: "destructive",
      });
    }
  }, [bulkSelectionState.selectedIds, isProcessing, getSelectedStocksData, toast]);
  
  const handleBulkFavorite = useCallback(() => {
    console.log("Bulk favorite:", Array.from(bulkSelectionState.selectedIds));
    // TODO: Implement bulk favorite
  }, [bulkSelectionState.selectedIds]);
  
  const handleBulkExport = useCallback(() => {
    console.log("Bulk export:", Array.from(bulkSelectionState.selectedIds));
    // TODO: Implement bulk export
  }, [bulkSelectionState.selectedIds]);

  // === DIALOG HANDLERS ===
  
  const handlePortfolioDialogClose = useCallback(() => {
    setIsPortfolioDialogOpen(false);
    setIsBulkMode(false);
    setSelectedStocks([]);
  }, []);

  

  // === COMPUTED DATA TRANSFORMATIONS ===
  
  // Transform selected stock for portfolio dialog
  const portfolioFund = useMemo(() => {
    if (isBulkMode || !selectedStock) return undefined;
    // Adapt StockRawRecord to FundRawRecord shape for portfolio display
    return {
      id: selectedStock.id,
      // Set all name fields to ensure proper display in portfolios
      display_name: `${selectedStock.name} (${selectedStock.code})`, // First priority for display
      name: selectedStock.name, // Second priority
      fund_name: selectedStock.name, // Third priority
      management_company_code: selectedStock.exchange,
      company_name: selectedStock.exchange_name || selectedStock.exchange,
      fund_code: selectedStock.code,
      description: `${selectedStock.name} (${selectedStock.code})`,
      // Required properties for FundRawRecord - MUST use 'stocks' for proper categorization
      sa_asset_class: 'stocks', // Always map stock records to 'stocks' asset class (displays as 'Equity')
      asset_class: 'stocks', // Also set standard asset_class field for portfolios
      sa_sub_asset_class: selectedStock.gicgroup || selectedStock.gicsector || 'Stock',
      creation_date: new Date().toISOString(),
      // Add ticker symbol as a more visible identifier
      ticker: selectedStock.code,
      ...selectedStock // Include all original stock properties too
    };
  }, [isBulkMode, selectedStock]);

  // Transform selected stocks for bulk portfolio dialog
  const portfolioFunds = useMemo(() => {
    if (!isBulkMode) return undefined;
    return selectedStocks.map(stock => ({
      id: stock.id,
      display_name: `${stock.name} (${stock.code})`,
      name: stock.name,
      fund_name: stock.name,
      management_company_code: stock.exchange,
      company_name: stock.exchange_name || stock.exchange,
      fund_code: stock.code,
      description: `${stock.name} (${stock.code})`,
      sa_asset_class: 'stocks',
      asset_class: 'stocks',
      sa_sub_asset_class: stock.gicgroup || stock.gicsector || 'Stock',
      creation_date: new Date().toISOString(),
      ticker: stock.code,
      ...stock
    }));
  }, [isBulkMode, selectedStocks]);

  // === RETURN INTERFACE ===
  return {
    // Filter states
    searchText,
    setSearchText,
    selectedType,
    setSelectedType,
    selectedExchange,
    setSelectedExchange,
    debouncedSearchText,
    
    // Count states
    totalCount,
    visibleCount,
    
    // Dialog states
    isPortfolioDialogOpen,
    selectedStock,
    selectedStocks,
    isBulkMode,
    
    // Bulk selection
    bulkSelectionState,
    bulkSelectionActions,
    
    // Event handlers
    handleCountsUpdate,
    handleFavorite,
    handleAddToWatchlist,
    handleAddToPortfolio,
    handleBulkAddToPortfolio,
    handleBulkFavorite,
    handleBulkExport,
    handlePortfolioDialogClose,
    handlePortfolioSuccess,
    
    // Table callback setter
    setGetSelectedStocksData,
    
    // Computed data
    portfolioFund,
    portfolioFunds
  };
};
